import _ from 'lodash';
import { useEffect, useState } from 'react';
import { AxiosResponse } from 'axios';
import { Row, Col, Card, ListGroup, Button, Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import ApiService from '../../services/ApiService';
import PaymentCardTransaction from './PaymentCardTransaction';
import { PaymentSessionSuccessResponse, Invoice } from './types';
import PaymentSession from './PaymentSession';
import CardPayment from './CardPayment';
import { modal } from '../modal/ModalEmitter';
import FlexAlert from '../FlexAlert';
import PaymentCurrencyInput from './PaymentCurrencyInput';

interface Props {
  customerNum?: string;
  sessionId?: string;
  onSuccess: (response: PaymentSessionSuccessResponse) => void;
  userInitials?: string;
}

const PaymentSessionRefund = ({ sessionId, onSuccess, userInitials: userInitialsProp = '' }: Props) => {
  const user = useSelector((state: any) => state.app.user);
  const [paymentSession, setPaymentSession] = useState<PaymentSession | null>();
  const [refunding, setRefunding] = useState(false);
  const [userInitials] = useState(userInitialsProp || user.ppc || '');
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice>();
  const [refundAmount, setRefundAmount] = useState('0');

  const canSubmit = !!refundAmount && selectedInvoice;

  useEffect(() => {
    ApiService.get('/payment/session', {
      params: {
        sessionId,
      },
    }).then((response: AxiosResponse) => {
      const sessions = response.data;

      if (sessions && sessions.length) {
        const session = new PaymentSession(sessions[0]);
        setPaymentSession(session);
      } else {
        setPaymentSession(null);
      }
    });
  }, []);

  useEffect(() => {
    if (!paymentSession) {
      return;
    }

    if (paymentSession.invoices.length === 1) {
      // only one invoice so auto select it
      setSelectedInvoice(paymentSession.invoices[0]);
    }
  }, [paymentSession]);

  if (paymentSession === null) {
    return (
      <FlexAlert modalClose>
        <h5>
          Credit card payment not found for session id <b className="text-warning">{sessionId}</b>
        </h5>
      </FlexAlert>
    );
  }

  if (!paymentSession) {
    // loading
    return null;
  }

  const handleRefund = () => {
    if (!selectedInvoice) {
      return;
    }

    modal.confirm({
      title: 'Refund',
      message: `Confirm refund of ${Number(refundAmount).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      })} back to card.`,
      ok: async () => {
        setRefunding(true);
        modal.close(); // close confirm

        const entries = [];
        let pendingRefundAmount = Number(refundAmount);
        try {
          for (const payment of paymentSession.cardPayments) {
            if (
              payment.transactionType === 'sale' &&
              !payment.isVoid() &&
              !payment.isFullyRefunded() &&
              payment.isApproved()
            ) {
              const { firstName, lastName } = payment.getName();

              let cardRefundAmount = pendingRefundAmount;
              if (pendingRefundAmount > payment.getNetAmount()) {
                // hit on multi card scenario. card sale did not have enough to fulfill refund
                // loop through each sale transaction until refund is fulfilled
                cardRefundAmount = payment.getNetAmount();
                pendingRefundAmount = parseFloat((pendingRefundAmount - payment.getNetAmount()).toFixed(2));
              }

              entries.push({
                address: '',
                amount: -1 * cardRefundAmount,
                cardBin: payment.cardBin,
                cardEntry: payment.cardEntry,
                cardExp: payment.cardExp,
                cardLast4: payment.cardLast4,
                cardType: payment.cardType,
                firstName,
                gateway: payment.gateway,
                hash: '',
                lastName,
                signature: '',
                terminalName: payment.terminalName,
                token: payment.cardToken,
                transactionId: payment.id,
                zip: '',
                transactionResult: payment.transactionResult,
              });

              await payment.doRefund({
                amount: cardRefundAmount,
                userInitials,
                sessionId: paymentSession.id,
                invoiceNum: selectedInvoice.invoiceNum,
                deposit: selectedInvoice.deposit || false,
              });

              if (!pendingRefundAmount) {
                // all done
                break;
              }
            }
          }

          modal.close(); // close self

          const invoices = [{ invoiceNum: selectedInvoice?.invoiceNum, amount: Number(refundAmount) }];

          onSuccess({
            transactions: entries,
            cash: 0,
            sessionId: paymentSession.id,
            invoices,
            customerNum: paymentSession.customerNum,
          });
        } catch (err) {
          console.error(err);
        }

        setRefunding(false);
      },
    });
  };

  const renderTransactions = () => {
    return paymentSession.cardPayments
      .filter((tx: CardPayment) => tx.transactionType === 'sale')
      .map((tx: CardPayment, i: number) => (
        <ListGroup.Item key={i}>
          <PaymentCardTransaction paymentTransaction={tx} userInitials={userInitials} readOnly />
        </ListGroup.Item>
      ));
  };

  const checkRefundAmount = (refundAmount: number) => {
    if (!selectedInvoice) {
      return refundAmount;
    }

    const { amount, invoiceNum } = selectedInvoice;
    const refunds = paymentSession.getRefundsByInvoice();
    const alreadyRefunded = refunds[invoiceNum];
    if (alreadyRefunded) {
      const refundableAmount = amount - alreadyRefunded;
      if (refundAmount > refundableAmount) {
        return refundableAmount;
      }
    }

    return refundAmount;
  };

  const handleBlur = () => {
    if (!selectedInvoice) {
      return;
    }

    const { amount } = selectedInvoice;

    // validate amounts
    let newRefundAmount = Number(refundAmount);
    if (newRefundAmount > amount) {
      newRefundAmount = amount;
    }

    if (newRefundAmount >= paymentSession.getTotalPaid()) {
      // Cannot refund more than what was charged
      let diff = newRefundAmount - paymentSession.getTotalPaid();
      if (diff > amount) {
        newRefundAmount = amount;
      } else if (diff < 0) {
        newRefundAmount = 0;
      }
    }

    newRefundAmount = checkRefundAmount(newRefundAmount);

    setRefundAmount(newRefundAmount.toFixed(2));
  };

  const handleChange = (value: string) => {
    setRefundAmount(value || '0');
  };

  const handleMax = () => {
    if (!selectedInvoice) {
      return;
    }

    setRefundAmount(checkRefundAmount(selectedInvoice.amount).toFixed(2));
  };

  const handleInvoiceSelect = (event: any) => {
    setRefundAmount('0');
    paymentSession.invoices.forEach(invoice => {
      if (invoice.invoiceNum === event.target.value) {
        setSelectedInvoice(invoice);
      }
    });
  };

  const renderInvoiceSelect = () => {
    return (
      <Form.Row>
        <Col>
          <Form.Group as={Col}>
            <Form.Label>Select Invoice</Form.Label>
            <Form.Control as="select" onChange={handleInvoiceSelect} value={selectedInvoice?.invoiceNum}>
              {paymentSession.invoices.length > 1 && <option></option>}
              {paymentSession.invoices.map(invoice => {
                return (
                  <option value={invoice.invoiceNum}>
                    {invoice.invoiceNum} (
                    {invoice.amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })})
                  </option>
                );
              })}
            </Form.Control>
          </Form.Group>
        </Col>
      </Form.Row>
    );
  };

  const renderRefundInput = () => {
    return (
      <Form.Row>
        <Col>
          <Form.Group as={Col}>
            <Form.Label>Refund Amount</Form.Label>
            <PaymentCurrencyInput
              value={String(refundAmount || '0')}
              onBlur={handleBlur}
              onMax={handleMax}
              onChange={handleChange}
            />
          </Form.Group>
        </Col>
      </Form.Row>
    );
  };

  return (
    <>
      <Row className="flex-grow-1">
        <Col md={12} className="p-3">
          <Card style={{ fontSize: '19px', backgroundColor: '#eee', borderRadius: '3px' }} className="mb-3">
            <Card.Body>
              Issue Refund for invoice(s) {paymentSession.invoices.map(invoice => invoice.invoiceNum).join(', ')}
            </Card.Body>
          </Card>
          <div>
            <ListGroup className="mb-3">{renderTransactions()}</ListGroup>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <hr />
        </Col>
      </Row>
      {renderInvoiceSelect()}
      {renderRefundInput()}
      {!!paymentSession.getTotalPaid() && (
        <Row>
          <Col className="text-right">
            <Button onClick={handleRefund} disabled={refunding || !canSubmit}>
              Execute Refund
            </Button>
          </Col>
        </Row>
      )}

      {!paymentSession.getTotalPaid() && (
        <Row>
          <Col className="text-center">Payment session cannot be refunded</Col>
        </Row>
      )}
    </>
  );
};

export default PaymentSessionRefund;
